import {
  PAYEE_TYPES,
  SECTIONS,
  type PayeeType,
  type Section,
  type VendorPatchBody,
  type VendorRow,
} from "@checkone/contract/client";
import { useEffect, useId, useRef, useState } from "react";
import { isApiError } from "../../api/client";
import { useApiMutation } from "../../api/hooks";
import { useToast } from "../../components/Toasts";
import { PAYEE_LABEL, sectionLabel } from "../../lib/format";

/** Edit identifiers, payee type and section. Every saved change lands in the vendor's history. */
export function EditVendorDialog({ vendor, onClose }: { vendor: VendorRow | null; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const patch = useApiMutation("vendors.patch", ["vendors.list", "vendors.history"]);
  const toast = useToast();
  const ids = {
    ntn: useId(),
    cnic: useId(),
    strn: useId(),
    payee: useId(),
    section: useId(),
    title: useId(),
  };
  const [ntn, setNtn] = useState("");
  const [cnic, setCnic] = useState("");
  const [strn, setStrn] = useState("");
  const [payee, setPayee] = useState<PayeeType | "">("");
  const [section, setSection] = useState<Section | "">("");
  const [fieldError, setFieldError] = useState<string | null>(null);

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (vendor) {
      setNtn(vendor.ntn ?? "");
      setCnic("");
      setStrn(vendor.strn ?? "");
      setPayee(vendor.payee_type === "unconfirmed" ? (vendor.payee_type_suggested ?? "") : vendor.payee_type);
      setSection(vendor.default_section ?? "");
      setFieldError(null);
      if (!d.open) d.showModal?.();
    } else if (d.open) d.close?.();
  }, [vendor]);

  if (!vendor) return <dialog ref={ref} className="modal" />;

  const submit = () => {
    const body: VendorPatchBody = { expected_updated_at: vendor.updated_at };
    if (ntn.trim() !== (vendor.ntn ?? "")) body.ntn = ntn.trim() || null;
    if (cnic.trim()) body.cnic = cnic.trim();
    if (strn.trim() !== (vendor.strn ?? "")) body.strn = strn.trim() || null;
    if (payee && payee !== vendor.payee_type) body.payee_type = payee;
    if ((section || null) !== vendor.default_section) body.default_section = section || null;
    if (Object.keys(body).length === 1) return onClose();
    patch.mutate(
      { params: { id: vendor.id }, body },
      {
        onSuccess: () => {
          toast.success(
            body.ntn !== undefined || body.cnic !== undefined
              ? `${vendor.display_name} updated. Checking it against FBR's current files now.`
              : `${vendor.display_name} updated.`,
          );
          onClose();
        },
        onError: (e) => {
          if (isApiError(e) && (e.code === "identifier_malformed" || e.code === "identifier_duplicate"))
            setFieldError(
              `${(typeof e.details?.field === "string" ? e.details.field : "identifier").toUpperCase()}: ${e.message}`,
            );
          else toast.error(e);
        },
      },
    );
  };

  return (
    <dialog
      ref={ref}
      className="modal"
      aria-labelledby={ids.title}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="modal-body">
          <h2 id={ids.title}>Edit {vendor.display_name}</h2>
          <p className="subtle" style={{ margin: 0 }}>
            CheckOne matches by identifier only. A new or changed NTN or CNIC is checked against FBR's current
            files a few seconds after you save.
          </p>
          {fieldError && (
            <div className="callout danger" role="alert">
              {fieldError}
            </div>
          )}
          <div className="field">
            <label htmlFor={ids.ntn}>NTN</label>
            <input
              id={ids.ntn}
              type="text"
              inputMode="text"
              autoComplete="off"
              value={ntn}
              onChange={(e) => setNtn(e.target.value)}
              placeholder="1234567-8 or A123456"
            />
          </div>
          <div className="field">
            <label htmlFor={ids.cnic}>CNIC</label>
            <input
              id={ids.cnic}
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={cnic}
              onChange={(e) => setCnic(e.target.value)}
              placeholder="35202-1234567-1"
              aria-describedby={`${ids.cnic}-hint`}
            />
            <span className="hint" id={`${ids.cnic}-hint`}>
              {vendor.cnic_masked ? (
                <>
                  Current: <span className="mono">{vendor.cnic_masked}</span>. Leave blank to keep it.
                </>
              ) : (
                "CNICs are personal data: we show them masked."
              )}
            </span>
          </div>
          <div className="field">
            <label htmlFor={ids.strn}>STRN</label>
            <input
              id={ids.strn}
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={strn}
              onChange={(e) => setStrn(e.target.value)}
            />
            <span className="hint">
              FBR's lists do not carry STRN; add an NTN or CNIC so the vendor can be checked.
            </span>
          </div>
          <div className="grid-2">
            <div className="field">
              <label htmlFor={ids.payee}>Payee type</label>
              <select id={ids.payee} value={payee} onChange={(e) => setPayee(e.target.value as PayeeType)}>
                <option value="">Not confirmed</option>
                {PAYEE_TYPES.map((p) => (
                  <option key={p} value={p}>
                    {PAYEE_LABEL[p]}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor={ids.section}>Withholding section</label>
              <select
                id={ids.section}
                value={section}
                onChange={(e) => setSection(e.target.value as Section)}
              >
                <option value="">Workspace default</option>
                {SECTIONS.map((s) => (
                  <option key={s} value={s}>
                    {sectionLabel(s)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn primary" disabled={patch.isPending}>
            {patch.isPending ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </dialog>
  );
}
